import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts"
import { type Filter, type Query } from "@cubejs-client/core"
import { PaginationState } from "@tanstack/react-table"
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline"

import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import { TableVisualization } from "~/ui-rtk/components/ui/charts"
import {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import ValueWithDelta from "~/ui-rtk/components/ui/common/ValueWithDelta/ValueWithDelta"
import { Button } from "~/ui-rtk/components/ui/controls"
import { H3 } from "~/ui-rtk/components/ui/typography"
import {
  calculatePercentageChange,
  convertNumber,
  type ConvertNumberType,
} from "~/ui-rtk/utils/number-utils"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { GranularityMapping } from "~/ui-rtk/constants/metrics-mapping"
import { exportTableToCSV } from "~/ui-rtk/utils/csv-export"
import useUrlSorting from "~/ui-rtk/hooks/url-sorting"
import useDrawer from "~/ui-rtk/hooks/drawer"
import { DRAWER_TYPE, type TDrawerConfig } from "~/ui-rtk/constants/drawer"
import useDate from "~/ui-rtk/hooks/date"
import { DATE_FORMAT } from "~/ui-rtk/hooks/period"
import { downloadSvgAsPng } from "../utils/download"
import { isEmptyDimensionValue } from "../connect"

const dayjs = useDate()

// Label a time bucket for the trend x-axis (month buckets show the month).
const formatBucket = (raw?: string, granularity?: AggregationType): string => {
  if (!raw) return "—"
  const d = dayjs(raw)
  if (!d.isValid()) return String(raw)
  return granularity === AggregationType.MONTHLY
    ? d.format("MMM YYYY")
    : d.format("MMM D")
}

// Format a measure cell using that row's own unit (currency/percent/…).
// Module-scoped so memos that use it don't need it as a dependency — it closes
// over nothing but the row it's given.
const renderMeasureCell = (key: "current" | "previous") => (row: TMetric) =>
  convertNumber(
    (row.numberType as ConvertNumberType) ?? "number",
    Number(row[key] ?? 0),
  )

/**
 * An ad with no tag row (or an empty column for this vertical) comes back from
 * cube as `null`, which the table renders as a bare "-" and the chart as "—".
 * Give that group the caller's label instead, on the grouping dimension only,
 * so the table, the chart axis, the CSV export, and the row-click handler all
 * agree on one value. Apply it to comparison rows as well as current ones —
 * `compareByX` pairs the two periods by this string, so labelling one side only
 * would silently drop the empty bucket's comparison.
 *
 * Returns a new object rather than mutating: callers today pass a freshly built
 * row, but a future refactor that hands over a `resultSet` item directly would
 * otherwise be writing into cube's own cached data. Module-scoped (not a
 * closure) so it takes no `useMemo` dependency and can't go stale.
 */
const labelEmptyDimension = (
  row: Record<string, unknown>,
  xKey: string,
  emptyLabel: string | undefined,
): Record<string, unknown> =>
  emptyLabel && isEmptyDimensionValue(row[xKey])
    ? { ...row, [xKey]: emptyLabel }
    : row

// Recharts spreads each data point into its shape's SVG props, so a point key
// named like a DOM prop (`style`, `name`, `width`, …) leaks onto the element —
// a dimension called "style" crashes React (#62). The x dimension is therefore
// remapped to this reserved key instead of using its raw name.
const CHART_X_KEY = "__x"

// Default series palette (indigo / green / amber / red / cyan / violet).
const PALETTE = [
  "#6366f1",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
  "#a855f7",
]

export type TReportSeries = {
  key: string
  label: string
  color?: string
  /** Per-measure display format (currency/percent/coef/…). */
  numberType?: ConvertNumberType
  /**
   * Y axis this series binds to in dimensioned charts (default "left").
   * Use "right" for small-magnitude series (e.g. ROAS) charted next to
   * large-magnitude ones (e.g. spend).
   */
  axis?: "left" | "right"
}

export type TReportChartConfig = {
  type: "bar" | "line"
  /** Dimension key (prefix-stripped) used for the chart category / x-axis. */
  xKey: string
  series: TReportSeries[]
  /** Default axis/tooltip format (first measure's format). */
  numberType?: ConvertNumberType
}

export type TReportProps = {
  title: string
  description?: string
  /** Cube query WITHOUT a date range — the global range is injected here. */
  query: Query
  chart: TReportChartConfig
  columns: TableVisualizationColumn[]
  /** Stable id for table state (column sizing, sorting) persistence. */
  chartId: string
  /** Global time range from the page. */
  dateRange: [Date, Date]
  /**
   * Optional comparison range. When set together with `comparisonEnabled`,
   * a dimensionless (measures-only) report renders current-vs-previous totals
   * with % deltas; a dimensioned report renders per-row previous values
   * (matched by the x dimension) in both the table and the chart.
   */
  compareRange?: [Date, Date]
  /** Enables comparison fetching/rendering when a `compareRange` is provided. */
  comparisonEnabled?: boolean
  /**
   * Time bucket for measures-only sections (daily/weekly/monthly). When set and
   * the cube has a time axis, the chart renders a trend; the table still shows
   * period totals. Ignored for dimensioned reports.
   */
  granularity?: AggregationType
  /**
   * Chart style for measures-only sections. "line" (or unset) keeps the legacy
   * adaptive default (line trend / metric bars); "bar" forces bars; "stack"
   * stacks the trend's measures. Ignored for dimensioned reports.
   */
  chartType?: "bar" | "line" | "stack"
  /** Time dimension to bind the range to. Defaults to `<prefix>.md_date`. */
  timeDimension?: string
  /**
   * Page-level cube filters appended to the section's own `query.filters`
   * (e.g. the taxonomy page's global filters bar).
   */
  extraFilters?: Filter[]
  /** Base name for downloaded files. Defaults to chartId. */
  filenameBase?: string
  /**
   * Label to substitute when the grouping dimension comes back null/empty,
   * making that group a named, clickable row instead of a bare "-".
   *
   * Opt-in, because the right wording depends on what the dimension means: the
   * creative-taxonomy page passes "(untagged)", but a custom report grouped by
   * campaign or country would be lying if it said the same. Left unset, empty
   * groups keep the previous behaviour — rendered as "-" and non-clickable.
   */
  emptyDimensionLabel?: string
  /**
   * Tag category slug the grouping dimension represents, forwarded to the
   * row-click drawer so it can compare the warehouse grouping against live
   * app assignments. Only the creative-taxonomy page sets it.
   */
  tagSlug?: string
  /** Called once the query settles (data or error) — used to stagger mounts. */
  onSettled?: () => void
}

/** Imperative handle so a parent (e.g. "Download all") can trigger exports. */
export type TReportHandle = {
  downloadCsv: () => void
  hasData: () => boolean
}

/**
 * Self-contained report unit: a chart card (with PNG download) over a fully
 * interactive table card (sortable + paginated, with CSV download). Feed it a
 * cube query and the global date range; it fetches, strips the cube prefix,
 * and renders both views from the same result set.
 */
const Report = forwardRef<TReportHandle, TReportProps>(
  (
    {
      title,
      description,
      query,
      chart,
      columns,
      chartId,
      dateRange,
      compareRange,
      comparisonEnabled,
      granularity,
      chartType,
      timeDimension,
      extraFilters,
      filenameBase,
      emptyDimensionLabel,
      tagSlug,
      onSettled,
    },
    ref,
  ) => {
    const chartRef = useRef<HTMLDivElement>(null)
    const [paginationState, setPaginationState] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    })

    // Cube member prefix (view/cube name), e.g. "creative_taxonomy_report".
    const prefix = useMemo(() => {
      const first =
        query.dimensions?.[0] ??
        query.measures?.[0] ??
        query.timeDimensions?.[0]?.dimension
      return first ? String(first).split(".")[0] : ""
    }, [query])

    // Comparison + granularity only apply to dimensionless (measures-only)
    // sections — these aggregate cleanly to period totals.
    const isSummary = (query.dimensions ?? []).length === 0

    // Header-click sorting: TableVisualization runs with manualSorting and
    // persists the clicked sort to the URL under this chartId; reading the same
    // key here feeds it back into the cube query as a server-side `order`.
    const { sorting } = useUrlSorting(chartId)

    // Jump back to the first page when the sort changes — the previous page
    // index points into a differently-ordered row set.
    const sortingKey = JSON.stringify(sorting)
    useEffect(() => {
      setPaginationState(prev => ({ ...prev, pageIndex: 0 }))
    }, [sortingKey])

    const { openDrawer } = useDrawer()

    // The section's time dimension (if the cube has a time axis). Custom queries
    // carry their own `timeDimensions`; legacy callers omit it -> `<prefix>.md_date`.
    const timeDimMember =
      query.timeDimensions === undefined
        ? (timeDimension ?? `${prefix}.md_date`)
        : query.timeDimensions[0]?.dimension
    const hasTimeDim = Boolean(timeDimMember)

    // Bucket the time axis only for measures-only sections with a chosen
    // granularity — never for dimensioned reports (would alter their grouping).
    const cubeGranularity =
      isSummary && hasTimeDim && granularity
        ? GranularityMapping[granularity]
        : undefined
    const isTimeSeries = Boolean(cubeGranularity)

    // Inject a given date range (and optional granularity) onto the query's time
    // dimension. Shared by the main and comparison queries.
    const buildQuery = useCallback(
      (range: [Date, Date]): Query => {
        const dateRangeStr: [string, string] = [
          dayjs(range[0]).format(DATE_FORMAT),
          dayjs(range[1]).format(DATE_FORMAT),
        ]
        // Section's own filters plus the page-level filters bar.
        const filters = [...(query.filters ?? []), ...(extraFilters ?? [])]
        // A user sort from the table header overrides the section's default
        // `order`. Sorting ids are prefix-stripped column keys, so the cube
        // prefix goes back on. Summary tables sort synthetic rows
        // (metric/current) that aren't cube members — leave their order alone.
        const order =
          !isSummary && sorting.length > 0
            ? sorting.map(
                ({ id, desc }) =>
                  [`${prefix}.${id}`, desc ? "desc" : "asc"] as [
                    string,
                    "desc" | "asc",
                  ],
              )
            : undefined
        // A default `limit` caps the client-side payload; a caller's own `limit`
        // in `query` overrides it (spread after).
        if (!timeDimMember) {
          return {
            limit: 5000,
            ...query,
            ...(filters.length > 0 ? { filters } : {}),
            ...(order ? { order } : {}),
            timeDimensions: [],
          }
        }
        return {
          limit: 5000,
          ...query,
          ...(filters.length > 0 ? { filters } : {}),
          ...(order ? { order } : {}),
          timeDimensions: [
            {
              dimension: timeDimMember,
              dateRange: dateRangeStr,
              ...(cubeGranularity ? { granularity: cubeGranularity } : {}),
            },
          ],
        }
      },
      [
        query,
        timeDimMember,
        cubeGranularity,
        extraFilters,
        sorting,
        isSummary,
        prefix,
      ],
    )

    const cubeQuery = useMemo<Query>(
      () => buildQuery(dateRange),
      [buildQuery, dateRange],
    )

    const compareEnabled = Boolean(comparisonEnabled && compareRange)
    const compareQuery = useMemo<Query>(
      () => buildQuery(compareRange ?? dateRange),
      [buildQuery, compareRange, dateRange],
    )

    const { resultSet, isLoading, error } = useCubeQuery(cubeQuery)
    const { resultSet: compareResultSet } = useCubeQuery(compareQuery, {
      skip: !compareEnabled,
    })

    // Only render comparison once the second query has resolved — otherwise the
    // in-flight window shows Previous=0 and a misleading +100% delta.
    const compareReady = compareEnabled && Boolean(compareResultSet)

    // Signal the parent once per load cycle when the query settles (data or
    // error). Guarded with a ref so it fires once even though `onSettled` is an
    // inline closure whose identity changes on every parent render.
    const settledRef = useRef(false)
    useEffect(() => {
      if (isLoading) {
        settledRef.current = false
        return
      }
      if (!settledRef.current && (resultSet || error)) {
        settledRef.current = true
        onSettled?.()
      }
    }, [isLoading, resultSet, error, onSettled])

    // Prefix-stripped rows for the table (values kept as-is; the table formats
    // them via column `type`). Each row needs a stable `id`.
    const rows = useMemo<Record<string, unknown>[]>(() => {
      if (!resultSet) return []
      return resultSet.rawData().map((item, idx) => {
        const row: Record<string, unknown> = { id: `${chartId}-${idx}` }
        Object.keys(item).forEach(key => {
          // Strip the cube/view prefix from member keys (`view.measure` ->
          // `measure`) so keys are short and dot-free (Recharts treats dots as
          // nested paths). Requires a single-cube query to avoid collisions —
          // enforced at save time in the custom-report editor.
          row[key.replace(/^[^.]+\./, "")] = item[key]
        })
        return labelEmptyDimension(row, chart.xKey, emptyDimensionLabel)
      })
    }, [resultSet, chartId, chart.xKey, emptyDimensionLabel])

    // Prefix-stripped comparison rows (only fetched when compareEnabled).
    const compareRows = useMemo<Record<string, unknown>[]>(() => {
      if (!compareResultSet) return []
      return compareResultSet.rawData().map((item, idx) => {
        const row: Record<string, unknown> = { id: `${chartId}-prev-${idx}` }
        Object.keys(item).forEach(key => {
          row[key.replace(/^[^.]+\./, "")] = item[key]
        })
        return labelEmptyDimension(row, chart.xKey, emptyDimensionLabel)
      })
    }, [compareResultSet, chartId, chart.xKey, emptyDimensionLabel])

    // Dimensioned comparison: previous-period rows pair with current rows by
    // the x dimension's value (not by index — sort order can differ between
    // the two periods).
    const hasDimCompare = !isSummary && compareEnabled && compareRows.length > 0

    // Numeric, coerced copy for Recharts (cube returns numbers as strings).
    // With an active comparison, each point also carries `<key>__prev`.
    const chartData = useMemo(() => {
      const compareByX = hasDimCompare
        ? new Map(compareRows.map(row => [String(row[chart.xKey] ?? "—"), row]))
        : null
      return rows.map(row => {
        const x = row[chart.xKey] ?? "—"
        const prev = compareByX?.get(String(x))
        const point: Record<string, unknown> = { [CHART_X_KEY]: x }
        chart.series.forEach(({ key }) => {
          point[key] = Number(row[key] ?? 0)
          if (compareByX) {
            point[`${key}__prev`] = Number(prev?.[key] ?? 0)
          }
        })
        return point
      })
    }, [rows, chart, compareRows, hasDimCompare])

    // Measures-only sections aggregate to a single totals row, but sum defensively
    // across rows so totals are correct for a granular time series too.
    const sumMeasure = (src: Record<string, unknown>[], key: string) =>
      src.reduce((acc, r) => acc + Number(r[key] ?? 0), 0)

    // One row per measure (carrying its own format) — drives the summary table
    // and the no-time-axis bar chart. Totals sum across rows, so this holds
    // whether the query returns a single totals row or a granular series.
    const summaryRows = useMemo(() => {
      if (!isSummary || rows.length === 0) return []
      return chart.series.map(({ key, label, numberType }) => {
        const current = sumMeasure(rows, key)
        const previous = compareReady ? sumMeasure(compareRows, key) : 0
        return {
          id: `${chartId}-sum-${key}`,
          metric: label,
          current,
          previous,
          // Raw number so CSV export carries the delta; the cell renders it via
          // ValueWithDelta instead.
          change: compareReady
            ? calculatePercentageChange(current, previous)
            : 0,
          numberType: numberType ?? chart.numberType ?? "number",
        }
      })
    }, [
      isSummary,
      rows,
      compareRows,
      compareReady,
      chart.series,
      chart.numberType,
      chartId,
    ])

    const summaryColumns = useMemo<TableVisualizationColumn[]>(() => {
      const cols: TableVisualizationColumn[] = [
        { key: "metric", header: "Metric", type: "text" },
        {
          key: "current",
          header: compareReady ? "Current" : "Value",
          // render handles per-row formatting; "text" avoids double-formatting.
          type: "text",
          render: renderMeasureCell("current"),
        },
      ]
      if (compareReady) {
        cols.push(
          {
            key: "previous",
            header: "Previous",
            type: "text",
            render: renderMeasureCell("previous"),
          },
          {
            key: "change",
            header: "Change",
            type: "text",
            render: (row: TMetric) => (
              <ValueWithDelta
                hideActualValue
                actualValue={Number(row.current ?? 0)}
                comparedValue={Number(row.previous ?? 0)}
              />
            ),
          },
        )
      }
      return cols
    }, [compareReady])

    // Trend points for the time-series chart: { date, <measure>... } per bucket.
    const seriesData = useMemo(() => {
      if (!isTimeSeries || !resultSet) return []
      const dateKey = `${timeDimMember}.${cubeGranularity}`
      return resultSet.rawData().map(item => {
        const rawDate = (item[dateKey] ?? item[timeDimMember as string]) as
          | string
          | undefined
        const point: Record<string, unknown> = {
          date: formatBucket(rawDate, granularity),
        }
        chart.series.forEach(({ key }) => {
          point[key] = Number(item[`${prefix}.${key}`] ?? 0)
        })
        return point
      })
    }, [
      isTimeSeries,
      resultSet,
      timeDimMember,
      cubeGranularity,
      granularity,
      prefix,
      chart.series,
    ])

    // Trend points with the comparison period merged in as `<key>__prev`
    // fields. Comparison buckets pair with current buckets by index (both
    // queries share the same granularity); a length mismatch leaves the
    // unmatched tail without a prev value rather than misaligning dates.
    const trendData = useMemo(() => {
      if (!compareReady || compareRows.length === 0) return seriesData
      return seriesData.map((point, idx) => {
        const prev = compareRows[idx]
        if (!prev) return point
        const merged: Record<string, unknown> = { ...point }
        chart.series.forEach(({ key }) => {
          merged[`${key}__prev`] = Number(prev[key] ?? 0)
        })
        return merged
      })
    }, [seriesData, compareRows, compareReady, chart.series])

    const hasTrendCompare =
      isTimeSeries && compareReady && compareRows.length > 0

    const summaryChartData = useMemo(
      () =>
        summaryRows.map(r => ({
          metric: r.metric,
          current: r.current,
          ...(compareReady ? { previous: r.previous } : {}),
        })),
      [summaryRows, compareReady],
    )

    // The on-screen table: transposed metric summary for measures-only sections
    // (period totals + Δ%), otherwise the standard prefix-stripped rows.
    const tableRows = isSummary ? summaryRows : rows
    const tableColumns = isSummary ? summaryColumns : columns

    // For a time series the table shows totals, but the CSV exports the full
    // per-bucket detail (Date + each measure) so the trend is downloadable.
    const timeSeriesColumns: TableVisualizationColumn[] = [
      { key: "date", header: "Date", type: "text" },
      ...chart.series.flatMap(s => [
        {
          key: s.key,
          header: s.label,
          type: s.numberType ?? ("number" as const),
        },
        ...(hasTrendCompare
          ? [
              {
                key: `${s.key}__prev`,
                header: `${s.label} (prev)`,
                type: s.numberType ?? ("number" as const),
              },
            ]
          : []),
      ]),
    ]
    const csvRows = isTimeSeries ? trendData : tableRows
    const csvColumns = isTimeSeries ? timeSeriesColumns : tableColumns

    const formatValue = (value: number): string =>
      String(convertNumber(chart.numberType ?? "number", value))

    // Dimensioned charts support a secondary right Y axis so series with very
    // different magnitudes (spend vs ROAS) stay readable. Each axis formats
    // ticks with its first series' numberType.
    const leftSeries = chart.series.filter(s => s.axis !== "right")
    const rightSeries = chart.series.filter(s => s.axis === "right")
    const hasRightAxis = rightSeries.length > 0
    const leftAxisNumberType =
      leftSeries[0]?.numberType ?? chart.numberType ?? "number"
    const rightAxisNumberType = rightSeries[0]?.numberType ?? "number"
    const formatAxisValue =
      (numberType: ConvertNumberType) => (value: number | string) =>
        String(convertNumber(numberType, Number(value)))

    // Tooltip formats each series by its own unit (matched by display name);
    // the comparison overlay ("<label> (prev)") shares its series' unit.
    const seriesFormatByLabel = new Map(
      chart.series.flatMap(s => {
        const numberType = s.numberType ?? chart.numberType ?? "number"
        return [
          [s.label, numberType],
          [`${s.label} (prev)`, numberType],
        ] as const
      }),
    )
    const formatSeriesValue = (value: number, name: string): string =>
      String(convertNumber(seriesFormatByLabel.get(name) ?? "number", value))

    const handleDownloadPng = async () => {
      const svg = chartRef.current?.querySelector("svg")
      if (svg) {
        await downloadSvgAsPng(
          svg as SVGSVGElement,
          `${filenameBase ?? chartId}-chart`,
        )
      }
    }

    const handleDownloadCsv = () => {
      exportTableToCSV(csvRows, csvColumns, {
        filename: `${filenameBase ?? chartId}.csv`,
      })
    }

    // Row click on a dimensioned table → drawer listing the ads aggregated
    // into that row: same section + page filters and date range, narrowed to
    // the row's dimension value. Drawer props must stay serializable.
    const handleRowClick = (row: TMetric) => {
      // With emptyDimensionLabel set, empty dimension values were normalised
      // at row-build time, so this guard never fires for them. It still fires
      // for consumers that leave the prop unset (Custom Reports), where an
      // empty group keeps its null value and stays non-clickable as before.
      // (Summary tables never reach here — their onRowClick is undefined.)
      const value = row[chart.xKey]
      if (value === undefined || value === null || String(value) === "") return
      openDrawer({
        clickSourceId: ".creative-taxonomy-report-page",
        type: DRAWER_TYPE.CampaingAdSetAdDetails,
        props: {
          component: "TaxonomyGroupAds",
          title,
          dimension: chart.xKey,
          tagSlug,
          value: String(value),
          prefix,
          filters: [...(query.filters ?? []), ...(extraFilters ?? [])],
          dateRange: [
            dayjs(dateRange[0]).format(DATE_FORMAT),
            dayjs(dateRange[1]).format(DATE_FORMAT),
          ] as [string, string],
          metricColumns: columns.filter(({ key }) => key !== chart.xKey),
        },
      } as TDrawerConfig)
    }

    const hasData = rows.length > 0
    const axisTick = { fill: "#94a3b8", fontSize: 12 }

    useImperativeHandle(
      ref,
      () => ({
        downloadCsv: handleDownloadCsv,
        hasData: () => hasData,
      }),
      [csvRows, csvColumns, hasData],
    )

    const renderChartBody = () => {
      if (error) {
        return (
          <div className="h-80 flex items-center justify-center text-error text-sm px-4 text-center">
            {error.message || "Failed to load chart data"}
          </div>
        )
      }
      if (isLoading && !hasData) {
        return (
          <div className="h-80 flex items-center justify-center">
            <Loader size={40} />
          </div>
        )
      }
      if (!hasData) {
        return (
          <div className="h-80 flex items-center justify-center text-system-gray-400 text-sm">
            No data for the selected time range.
          </div>
        )
      }

      // Measures-only sections with a time axis + chosen granularity render a
      // per-measure trend over the bucketed dates. Style follows the section's
      // chartType: line (default) vs grouped/stacked bars. When a comparison
      // range is active, the previous period overlays as dashed same-color
      // lines (bucket i vs bucket i), and the table still shows period totals.
      if (isTimeSeries) {
        const trendAsBars = chartType === "bar" || chartType === "stack"
        return (
          <div ref={chartRef} className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={trendData}
                margin={{ top: 16, right: 24, bottom: 8, left: 8 }}
              >
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={axisTick} stroke="#334155" />
                <YAxis
                  tick={axisTick}
                  stroke="#334155"
                  tickFormatter={v => formatValue(Number(v))}
                />
                <Tooltip
                  cursor={trendAsBars ? { fill: "#1e293b55" } : undefined}
                  contentStyle={{
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 8,
                  }}
                  labelStyle={{ color: "#e2e8f0" }}
                  formatter={(value: number | string, name: string) =>
                    formatSeriesValue(Number(value), name)
                  }
                />
                <Legend wrapperStyle={{ color: "#94a3b8" }} />
                {chart.series.map((s, idx) =>
                  trendAsBars ? (
                    <Bar
                      key={s.key}
                      dataKey={s.key}
                      name={s.label}
                      // ponytail: stacking mixed-unit measures (spend + ROAS) is
                      // on the user; no unit-guard, they picked both the measures
                      // and the stack type.
                      stackId={chartType === "stack" ? "stack" : undefined}
                      fill={s.color ?? PALETTE[idx % PALETTE.length]}
                      radius={[4, 4, 0, 0]}
                    />
                  ) : (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.label}
                      stroke={s.color ?? PALETTE[idx % PALETTE.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ),
                )}
                {/* Comparison mirrors the primary mark type so it reads as
                    "same metric, previous period": translucent dashed-outline
                    bars beside solid bars (a parallel stack when stacked),
                    dashed lines beside solid lines. */}
                {hasTrendCompare &&
                  chart.series.map((s, idx) =>
                    trendAsBars ? (
                      <Bar
                        key={`${s.key}__prev`}
                        dataKey={`${s.key}__prev`}
                        name={`${s.label} (prev)`}
                        stackId={
                          chartType === "stack" ? "stack-prev" : undefined
                        }
                        fill={s.color ?? PALETTE[idx % PALETTE.length]}
                        fillOpacity={0.3}
                        stroke={s.color ?? PALETTE[idx % PALETTE.length]}
                        strokeWidth={1}
                        strokeOpacity={0.6}
                        strokeDasharray="4 3"
                        radius={[4, 4, 0, 0]}
                      />
                    ) : (
                      <Line
                        key={`${s.key}__prev`}
                        type="monotone"
                        dataKey={`${s.key}__prev`}
                        name={`${s.label} (prev)`}
                        stroke={s.color ?? PALETTE[idx % PALETTE.length]}
                        strokeWidth={1.5}
                        strokeDasharray="6 4"
                        strokeOpacity={0.55}
                        dot={false}
                      />
                    ),
                  )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )
      }

      // Measures-only sections without a time axis render one mark per metric
      // (current, plus the previous period when comparison is on). "line" draws
      // those as points/lines; bar/stack both render bars (stacking is only
      // meaningful on the time-series trend above).
      if (isSummary) {
        const summaryAsLine = chartType === "line"
        return (
          <div ref={chartRef} className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              {summaryAsLine ? (
                <LineChart
                  data={summaryChartData}
                  margin={{ top: 16, right: 24, bottom: 8, left: 8 }}
                >
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="metric"
                    tick={axisTick}
                    stroke="#334155"
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={axisTick}
                    stroke="#334155"
                    tickFormatter={v => formatValue(Number(v))}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#0f172a",
                      border: "1px solid #334155",
                      borderRadius: 8,
                    }}
                    labelStyle={{ color: "#e2e8f0" }}
                    formatter={(value: number | string) =>
                      formatValue(Number(value))
                    }
                  />
                  <Legend wrapperStyle={{ color: "#94a3b8" }} />
                  <Line
                    type="monotone"
                    dataKey="current"
                    name={compareReady ? "Current" : "Value"}
                    stroke={PALETTE[0]}
                    strokeWidth={2}
                  />
                  {compareReady && (
                    <Line
                      type="monotone"
                      dataKey="previous"
                      name="Previous"
                      stroke={PALETTE[0]}
                      strokeWidth={1.5}
                      strokeDasharray="6 4"
                      strokeOpacity={0.55}
                    />
                  )}
                </LineChart>
              ) : (
                <BarChart
                  data={summaryChartData}
                  margin={{ top: 16, right: 24, bottom: 8, left: 8 }}
                >
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="metric"
                    tick={axisTick}
                    stroke="#334155"
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={axisTick}
                    stroke="#334155"
                    tickFormatter={v => formatValue(Number(v))}
                  />
                  <Tooltip
                    cursor={{ fill: "#1e293b55" }}
                    contentStyle={{
                      background: "#0f172a",
                      border: "1px solid #334155",
                      borderRadius: 8,
                    }}
                    labelStyle={{ color: "#e2e8f0" }}
                    formatter={(value: number | string) =>
                      formatValue(Number(value))
                    }
                  />
                  <Legend wrapperStyle={{ color: "#94a3b8" }} />
                  <Bar
                    dataKey="current"
                    name={compareReady ? "Current" : "Value"}
                    fill={PALETTE[0]}
                    radius={[4, 4, 0, 0]}
                  />
                  {compareReady && (
                    <Bar
                      dataKey="previous"
                      name="Previous"
                      fill={PALETTE[0]}
                      fillOpacity={0.3}
                      stroke={PALETTE[0]}
                      strokeWidth={1}
                      strokeOpacity={0.6}
                      strokeDasharray="4 3"
                      radius={[4, 4, 0, 0]}
                    />
                  )}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )
      }

      return (
        <div ref={chartRef} className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {chart.type === "line" ? (
              <LineChart
                data={chartData}
                margin={{ top: 16, right: 24, bottom: 8, left: 8 }}
              >
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey={CHART_X_KEY} tick={axisTick} stroke="#334155" />
                <YAxis
                  yAxisId="left"
                  tick={axisTick}
                  stroke="#334155"
                  tickFormatter={formatAxisValue(leftAxisNumberType)}
                />
                {hasRightAxis && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={axisTick}
                    stroke="#334155"
                    tickFormatter={formatAxisValue(rightAxisNumberType)}
                  />
                )}
                <Tooltip
                  contentStyle={{
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 8,
                  }}
                  labelStyle={{ color: "#e2e8f0" }}
                  formatter={(value: number | string, name: string) =>
                    formatSeriesValue(Number(value), name)
                  }
                />
                <Legend wrapperStyle={{ color: "#94a3b8" }} />
                {chart.series.flatMap((s, idx) => {
                  const color = s.color ?? PALETTE[idx % PALETTE.length]
                  const yAxisId = s.axis === "right" ? "right" : "left"
                  return [
                    <Line
                      key={s.key}
                      yAxisId={yAxisId}
                      type="monotone"
                      dataKey={s.key}
                      name={s.label}
                      stroke={color}
                      strokeWidth={2}
                      dot={false}
                    />,
                    ...(hasDimCompare
                      ? [
                          <Line
                            key={`${s.key}__prev`}
                            yAxisId={yAxisId}
                            type="monotone"
                            dataKey={`${s.key}__prev`}
                            name={`${s.label} (prev)`}
                            stroke={color}
                            strokeWidth={1.5}
                            strokeDasharray="6 4"
                            strokeOpacity={0.55}
                            dot={false}
                          />,
                        ]
                      : []),
                  ]
                })}
              </LineChart>
            ) : (
              <BarChart
                data={chartData}
                margin={{ top: 16, right: 24, bottom: 8, left: 8 }}
              >
                {/* Diagonal-stripe fill (one per series color) marking the
                    comparison period's bars. */}
                {hasDimCompare && (
                  <defs>
                    {chart.series.map((s, idx) => {
                      const color = s.color ?? PALETTE[idx % PALETTE.length]
                      return (
                        <pattern
                          key={s.key}
                          id={`${chartId}-prev-stripe-${idx}`}
                          patternUnits="userSpaceOnUse"
                          width={6}
                          height={6}
                          patternTransform="rotate(45)"
                        >
                          <rect
                            width={6}
                            height={6}
                            fill={color}
                            fillOpacity={0.12}
                          />
                          <line
                            x1={0}
                            y1={0}
                            x2={0}
                            y2={6}
                            stroke={color}
                            strokeWidth={2.5}
                            strokeOpacity={0.75}
                          />
                        </pattern>
                      )
                    })}
                  </defs>
                )}
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis
                  dataKey={CHART_X_KEY}
                  tick={axisTick}
                  stroke="#334155"
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  yAxisId="left"
                  tick={axisTick}
                  stroke="#334155"
                  tickFormatter={formatAxisValue(leftAxisNumberType)}
                />
                {hasRightAxis && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={axisTick}
                    stroke="#334155"
                    tickFormatter={formatAxisValue(rightAxisNumberType)}
                  />
                )}
                <Tooltip
                  cursor={{ fill: "#1e293b55" }}
                  contentStyle={{
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 8,
                  }}
                  labelStyle={{ color: "#e2e8f0" }}
                  formatter={(value: number | string, name: string) =>
                    formatSeriesValue(Number(value), name)
                  }
                />
                <Legend wrapperStyle={{ color: "#94a3b8" }} />
                {chart.series.flatMap((s, idx) => {
                  const color = s.color ?? PALETTE[idx % PALETTE.length]
                  const yAxisId = s.axis === "right" ? "right" : "left"
                  return [
                    <Bar
                      key={s.key}
                      yAxisId={yAxisId}
                      dataKey={s.key}
                      name={s.label}
                      fill={color}
                      radius={[4, 4, 0, 0]}
                    />,
                    ...(hasDimCompare
                      ? [
                          <Bar
                            key={`${s.key}__prev`}
                            yAxisId={yAxisId}
                            dataKey={`${s.key}__prev`}
                            name={`${s.label} (prev)`}
                            fill={`url(#${chartId}-prev-stripe-${idx})`}
                            stroke={color}
                            strokeWidth={1}
                            strokeOpacity={0.6}
                            strokeDasharray="4 3"
                            radius={[4, 4, 0, 0]}
                          />,
                        ]
                      : []),
                  ]
                })}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-system-sm">
        <div className="flex items-end justify-between gap-system-sm">
          <div>
            <H3 className="text-white font-bold text-lg">{title}</H3>
            {description && (
              <p className="text-sm text-system-gray-400 mt-0.5">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Chart card */}
        <Card
          rounded
          className="p-system-md bg-basic-dark-blue border border-basic-blue relative"
        >
          <div className="flex justify-end mb-2">
            <Button
              size="sm"
              variant={{ variant: "outlined" }}
              onClick={handleDownloadPng}
              disabled={!hasData}
            >
              <span className="flex items-center gap-2">
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download
              </span>
            </Button>
          </div>
          {renderChartBody()}
        </Card>

        {/* Table card */}
        <Card
          rounded
          className="p-system-md bg-basic-dark-blue border border-basic-blue"
        >
          <div className="flex justify-end mb-2">
            <Button
              size="sm"
              variant={{ variant: "outlined" }}
              onClick={handleDownloadCsv}
              disabled={!hasData}
            >
              <span className="flex items-center gap-2">
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download
              </span>
            </Button>
          </div>
          <TableVisualization
            chartId={chartId}
            // Compare mode expects [currentRows, previousRows]; cells match
            // previous rows by compareUniqueKey (the section's x dimension).
            metrics={
              (hasDimCompare
                ? [tableRows, compareRows]
                : tableRows) as TMetric[]
            }
            compareMode={hasDimCompare}
            compareUniqueKey={hasDimCompare ? chart.xKey : undefined}
            columnConfig={tableColumns}
            paginationState={paginationState}
            setPaginationState={setPaginationState}
            enablePagination
            manualPagination={false}
            onRowClick={isSummary ? undefined : handleRowClick}
          />
        </Card>
      </div>
    )
  },
)

export default Report
